import { useObserver } from 'mobx-react';
import { FC, useState } from 'react';
import {
    AppText,
    Button,
    ButtonRow,
    Header,
    ListView,
    Modal,
    Row,
    styled,
    Theme,
    ToastTypes,
} from '@streem/ui-react';
import { APITypes, StreemAPI } from '@streem/api';
import { useListGroupUsersStore } from '../../../hooks/list_store_hooks';
import { useActiveCompanyCode } from '../../../hooks/use_active_company_code';
import { columnBuilder } from './group_list_columns';
import { Redirect } from 'react-router-dom';
import { ColumnHeader } from '../../lists/list_view_with_pagination';
import appLogger from '../../../util/logging/app_logger';
import { useGlobalStore } from '../../../hooks/use_global_context';

const log = appLogger.extend('Groups Details Page');

interface GroupUserRow extends APITypes.StreemApiUser {
    key: string;
}

const GroupUsersList: FC<React.PropsWithChildren<{ groupName: string }>> = ({ groupName }) => {
    const groupUsersStore = useListGroupUsersStore();
    const [activeRecord, setActiveRecord] = useState<GroupUserRow>();
    const companyCode = useActiveCompanyCode();
    const { uiStore } = useGlobalStore();

    const [isOpen, setIsOpen] = useState(false);

    return useObserver(() => {
        const groups = groupUsersStore.results.reduce((acc, agent) => {
            if (acc.findIndex(a => a.sid === agent.sid) === -1) {
                acc.push(agent);
                return acc;
            } else {
                return acc;
            }
        }, []);

        const removeMemberFromGroup = async () => {
            try {
                await StreemAPI.groups.deleteGroupUser(companyCode, groupName, activeRecord.sid);
                groupUsersStore.refresh();
                setIsOpen(false);
                uiStore.addToast({
                    flavor: ToastTypes.SUCCESS,
                    id: `user-deleted-from-group-${activeRecord.sid}`,
                    content: 'User successfully removed from group',
                });
            } catch (e) {
                log.error('Failed to remove user from group:', e);
                uiStore.addToast({
                    flavor: ToastTypes.ERROR,
                    id: `user-deleted-from-group-error-${activeRecord.sid}`,
                    // @ts-expect-error - Improve typing here or update ToasterItem interface
                    content: e,
                });
            }
        };

        const handleDelete = (row: GroupUserRow, e: any) => {
            e.stopPropagation();
            e.preventDefault();
            setIsOpen(true);
            setActiveRecord(row);
        };

        const columns = [
            columnBuilder.nameColumn(false),
            columnBuilder.deleteColumn({ handleDelete }),
        ];

        return groupUsersStore.lastError && groupUsersStore.lastError.status === 500 ? (
            <Redirect to="/error" />
        ) : (
            <>
                <ListViewGrid columns={'1fr 100px'} style={{ boxShadow: Theme.shadow.bottom }}>
                    {columns.map(column => (
                        <ColumnHeader
                            key={column.key}
                            column={column}
                            sortOrder={'ASC'}
                            contentJustify={column.contentJustify}
                        />
                    ))}
                </ListViewGrid>
                <ScrollableContainer>
                    <ListView
                        columns={columns}
                        data={groups.map(u => ({ key: u.sid!, ...u } as GroupUserRow))}
                        loading={groupUsersStore.loading}
                        loadingMore={groupUsersStore.loadingMore}
                        data-testid="group-list"
                        buildRowTestId={(row: any) => `group-row-${row.name}`}
                        gridTemplateColumns="1fr 100px"
                    />
                </ScrollableContainer>
                <DeleteConfirmationModal
                    isOpen={isOpen}
                    closeModal={() => {
                        setIsOpen(false);
                    }}
                    handleDelete={removeMemberFromGroup}
                    selectedUser={activeRecord?.name}
                    groupName={groupName}
                />
            </>
        );
    });
};

export default GroupUsersList;

const ListViewGrid = styled.div<{ columns?: string }>(({ columns }) => ({
    display: 'grid',
    gridTemplateColumns: columns ?? '1fr auto auto',
    padding: '0 10px',
}));

const ScrollableContainer = styled.div({
    padding: '0 10px',
    overflowX: 'auto',
});

interface DeleteConfirmationModalProps {
    isOpen: boolean;
    closeModal: () => void;
    groupName: string;
    handleDelete: () => void;
    selectedUser: string;
}

const DeleteConfirmationModal: React.FC<React.PropsWithChildren<DeleteConfirmationModalProps>> = ({
    isOpen,
    closeModal,
    groupName,
    handleDelete,
    selectedUser,
}) => {
    return (
        <Modal
            data-testid="confirm-remove-team-member-modal"
            onClose={closeModal}
            isOpen={isOpen}
            styleOverride={{
                content: {
                    maxWidth: '600px',
                    padding: Theme.spacing.l,
                },
            }}
        >
            <Row mb={4}>
                <Header
                    data-testid="group-member-removal-warning-header"
                    size="large"
                    data-clarity-mask="true"
                >
                    {`Are you sure you want to remove ${selectedUser} from ${groupName}?`}
                </Header>
            </Row>
            <Row mb={5}>
                <AppText>This cannot be undone.</AppText>
            </Row>
            <ModalButtonRow>
                <Button variant="secondary" onClick={closeModal}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleDelete}
                    data-testid="delete-team-member-confirm-button"
                >
                    Delete
                </Button>
            </ModalButtonRow>
        </Modal>
    );
};

const ModalButtonRow = styled(ButtonRow)({
    justifyContent: 'flex-end',
    width: '100%',
});
